import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ENVIRONMENT_CONFIG, EnvironmentConfig, EnvironmentConfigData } from '@shared/graphql/queries/environmentConfig';

interface SiteConfigContextType {
  config: EnvironmentConfig | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

// Default fallback configuration
const DEFAULT_CONFIG: EnvironmentConfig = {
  siteName: 'TippingIQ',
  siteDomain: 'tippingiq.com',
  siteTagline: 'Punt like a pro, without the know!',
  contactEmail: 'contact@tippingiq.com',
  supportEmail: 'support@tippingiq.com',
  paywallEnabled: true
};

interface SiteConfigProviderProps {
  children: React.ReactNode;
}

export const SiteConfigProvider: React.FC<SiteConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<EnvironmentConfig>(DEFAULT_CONFIG);
  
  const { data, loading, error, refetch } = useQuery<EnvironmentConfigData>(GET_ENVIRONMENT_CONFIG, {
    errorPolicy: 'all', // Continue with default config if query fails
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first', // Use cache first, then network
  });

  useEffect(() => {
    if (data?.environmentConfig) {
      setConfig(data.environmentConfig);
    } else if (error) {
      // If there's an error, stick with the default config
      console.warn('Failed to load site configuration, using defaults:', error);
      setConfig(DEFAULT_CONFIG);
    }
  }, [data, error]);

  const contextValue: SiteConfigContextType = {
    config,
    loading,
    error: error || null,
    refetch: () => {
      refetch().catch(err => {
        console.warn('Failed to refetch site configuration:', err);
      });
    }
  };

  return (
    <SiteConfigContext.Provider value={contextValue}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = (): SiteConfigContextType => {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};

// Convenience hooks for specific config values
export const useSiteName = (): string => {
  const { config } = useSiteConfig();
  return config?.siteName || DEFAULT_CONFIG.siteName;
};

export const useSiteDomain = (): string => {
  const { config } = useSiteConfig();
  return config?.siteDomain || DEFAULT_CONFIG.siteDomain;
};

export const useSiteTagline = (): string => {
  const { config } = useSiteConfig();
  return config?.siteTagline || DEFAULT_CONFIG.siteTagline;
};

export const useContactEmail = (): string => {
  const { config } = useSiteConfig();
  return config?.contactEmail || DEFAULT_CONFIG.contactEmail;
};

export const useSupportEmail = (): string => {
  const { config } = useSiteConfig();
  return config?.supportEmail || DEFAULT_CONFIG.supportEmail;
};
